import { UserSettingsDto } from './user-settings.model';

/**
 * Mirrors DentalUI's LoginResponseDto including helper methods.
 */
export class LoginResponseDto {
  refreshToken = "";
  expiration?: string;
  userId = "";
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  isAdmin?: boolean;
  /**
   * Autobar seat assigned to this user.
   * When true, the user may open the CAD / Autobar app from design projects.
   */
  isDesignBar?: boolean | null;
  role?: string | null;
  permission?: string;
  communicationId?: string;
  accessToken = "";
  refreshTokenExpired?: Date;
  userAvatarByte?: string | null;
  userAvatarUrl?: string;
  type?: number | null;
  organizationId?: string;
  organizationDescription?: string;
  branchId?: string;
  branchDescription?: string;
  userSettingsDto: UserSettingsDto = new UserSettingsDto();

  setData(data: LoginResponseDto | Record<string, unknown>): void {
    const raw = data as Record<string, unknown>;
    // API camelCase veya PascalCase dönebilir (CommunicationId vs communicationId)
    const pick = <T>(camel: keyof LoginResponseDto, pascal: string): T | undefined => {
      const fromCamel = (data as LoginResponseDto)[camel];
      if (fromCamel !== undefined && fromCamel !== null && fromCamel !== '') {
        return fromCamel as T;
      }
      const fromPascal = raw[pascal];
      if (fromPascal !== undefined && fromPascal !== null && fromPascal !== '') {
        return fromPascal as T;
      }
      return fromCamel as T | undefined;
    };

    this.refreshToken = pick<string>('refreshToken', 'RefreshToken') ?? '';
    this.expiration = pick<string>('expiration', 'Expiration');
    this.userId = pick<string>('userId', 'UserId') ?? '';
    this.email = pick<string>('email', 'Email');
    this.phone = pick<string>('phone', 'Phone');
    this.firstName = pick<string>('firstName', 'FirstName');
    this.lastName = pick<string>('lastName', 'LastName');
    this.isAdmin = pick<boolean>('isAdmin', 'IsAdmin');
    this.isDesignBar = pick<boolean | null>('isDesignBar', 'IsDesignBar') ?? null;
    this.role = pick<string | null>('role', 'Role');
    this.permission = pick<string>('permission', 'Permission');
    this.communicationId = pick<string>('communicationId', 'CommunicationId');
    this.accessToken = pick<string>('accessToken', 'AccessToken') ?? '';
    this.refreshTokenExpired = pick<Date>('refreshTokenExpired', 'RefreshTokenExpired');
    this.userAvatarByte = pick<string | null>('userAvatarByte', 'UserAvatarByte');
    this.userAvatarUrl = pick<string>('userAvatarUrl', 'UserAvatarUrl');
    this.type = pick<number | null>('type', 'Type');
    this.organizationId = pick<string>('organizationId', 'OrganizationId');
    this.organizationDescription = pick<string>(
      'organizationDescription',
      'OrganizationDescription'
    );
    this.branchId = pick<string>('branchId', 'BranchId');
    this.branchDescription = pick<string>('branchDescription', 'BranchDescription');

    const settings =
      (data as LoginResponseDto).userSettingsDto ??
      (raw['UserSettingsDto'] as UserSettingsDto | undefined);
    if (settings) {
      this.userSettingsDto.setData(settings);
    }
  }

  clearData(): void {
    this.refreshToken = "";
    this.expiration = undefined;
    this.userId = '';
    this.email = '';
    this.phone = '';
    this.firstName = '';
    this.lastName = '';
    this.isAdmin = false;
    this.isDesignBar = null;
    this.role = null;
    this.permission = '';
    this.communicationId = '';
    this.accessToken = '';
    this.refreshTokenExpired = new Date();
    this.userAvatarByte = null;
    this.userAvatarUrl = '';
    this.type = null;
    this.organizationId = '';
    this.organizationDescription = '';
    this.branchId = '';
    this.branchDescription = '';
  }
}

export type LoginResponse = LoginResponseDto;

